import * as React from 'react';
import '../../scss/00_styleguide/_faux-input.scss';
import { mergeTests } from '../../test-utilities/mergeTests';
import { extractFormCellProps, FormCell, IFormCellProps } from '../FormCell';
import { DateInput } from '../DateInput';
import { CheckboxInput, Grid, Select } from 'ui-toolkit/index';
import { DateTime } from 'luxon';

export interface IMultiDateInputProps extends IFormCellProps {
  /** The date value attribute of the end date input. Date must be in ISO8601 Date format: YYYY-MM-DD. */
  endValue: string | null;
  /** The date value attribute of the start date input. Date must be in ISO8601 Date format: YYYY-MM-DD. */
  startValue: string | null;
  /** A callback fired when the date value prop changes. */
  onChange: (startValue: string | null, endValue: string | null) => void;
  dataTestIdStart: string;
  dataTestIdEnd: string;
  hideSelectMenu?: boolean;
}

type MultiDateInputSelectionMode = 'single' | 'minimum' | 'maximum';
type MultiDateInputSelectOption = { label: string; value: MultiDateInputSelectionMode };

const selectOptions: MultiDateInputSelectOption[] = [
  { label: 'Selected Date', value: 'single' },
  { label: 'After Date', value: 'minimum' },
  { label: 'Before Date', value: 'maximum' },
];

const determineSelectionMode = (startValue: string | null, endValue: string | null) => {
  if (startValue && !endValue) {
    return selectOptions[1];
  }
  if (!startValue && endValue) {
    return selectOptions[2];
  }
  return selectOptions[0];
};

// Calculates the initial value for the first calendar
const calculateFirstDate = (startDate: string | null, endDate: string | null) => {
  if (startDate !== null) {
    return startDate;
  }
  return endDate;
};

// Calculates the initial value for the second calendar
const calculateSecondDate = (startDate: string | null, endDate: string | null) => {
  if (startDate !== null && endDate !== null) {
    return endDate;
  }
  return null;
};

/**
 * A date input is a non-text input form element. You can select a single date from a popup calendar.
 * More info in {@link Calendar}
 */
export const MultiDateInput: React.FC<IMultiDateInputProps> = (props) => {
  const { formCellProps, inputProps } = extractFormCellProps(props);
  const { startValue, endValue, label, hideSelectMenu } = inputProps;
  const [isDateRange, setIsDateRange] = React.useState(startValue !== null && endValue !== null);
  const [firstDate, setFirstDate] = React.useState(calculateFirstDate(startValue, endValue));
  const [secondDate, setSecondDate] = React.useState(calculateSecondDate(startValue, endValue));
  const [selectionMode, setSelectionMode] = React.useState(determineSelectionMode(startValue, endValue));
  const dataTestClass = props['data-test-class'] || 'multi-date-input';

  let wrappingDiv = React.createRef<HTMLDivElement>();

  const onSingleDateChange = (firstDate: string | null, selectValue: MultiDateInputSelectOption) => {
    switch (selectValue.value) {
      case 'single':
        props.onChange(firstDate, addOneDay(firstDate));
        break;
      case 'minimum':
        props.onChange(firstDate, null);
        break;
      case 'maximum':
        props.onChange(null, firstDate);
        break;
    }
  };

  const checkboxChange = (value: boolean) => {
    setIsDateRange(value);
    if (value) {
      props.onChange(firstDate, secondDate);
    } else {
      setSecondDate(null);
      onSingleDateChange(firstDate, selectionMode);
    }
  };

  const addOneDay = (date: string | null) => {
    return date && DateTime.fromISO(date).plus({ days: 1 }).toISODate();
  };

  const selectChange = (value: MultiDateInputSelectOption) => {
    setSelectionMode(value);
    onSingleDateChange(firstDate, value);
  };

  return (
    <div className={formCellProps.className} ref={wrappingDiv}>
      <FormCell {...formCellProps} data-test-class={dataTestClass}>
        <Grid columnCount={2}>
          <DateInput
            label={label}
            value={firstDate}
            data-test-class={mergeTests(dataTestClass, 'date-input')}
            data-test-id={mergeTests(props.dataTestIdStart, 'date-input')}
            errorText={!!formCellProps.errorText}
            onChange={(value) => {
              setFirstDate(value.value);
              if (isDateRange) {
                props.onChange(value.value, secondDate);
              } else {
                onSingleDateChange(value.value, selectionMode);
              }
            }}
          >
            <CheckboxInput text={'Date Range'} onChange={checkboxChange} value={isDateRange} />
          </DateInput>
          {isDateRange ? (
            <DateInput
              value={secondDate}
              minDate={firstDate!}
              data-test-class={mergeTests(dataTestClass, 'date-input')}
              data-test-id={mergeTests(props.dataTestIdEnd, 'date-input')}
              errorText={!!formCellProps.errorText}
              onChange={(value) => {
                setSecondDate(value.value);
                props.onChange(firstDate, value.value);
              }}
            />
          ) : hideSelectMenu ? null : (
            <Select
              value={selectionMode}
              onChange={selectChange}
              options={selectOptions}
              data-test-class={mergeTests(dataTestClass, 'select')}
              data-test-id={mergeTests(props.dataTestIdStart, props.dataTestIdEnd, 'select')}
            />
          )}
        </Grid>
      </FormCell>
    </div>
  );
};
